import { useEffect, useMemo, useRef, useState } from 'react'

export type ChecklistOption = { id: string; label: string; group?: string }

/**
 * A filter button that opens a checklist (search box, Select all, Clear).
 * Empty selection = no filter.
 */
export default function FilterChecklist({
  label,
  options,
  selected,
  onChange,
  searchable = false,
  emptyText = 'Nothing to pick',
}: {
  label: string
  options: ChecklistOption[]
  selected: string[]
  onChange: (next: string[]) => void
  searchable?: boolean
  emptyText?: string
}) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const rootRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    function onDoc(e: MouseEvent) {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false)
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDoc)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase()
    return q ? options.filter((o) => o.label.toLowerCase().includes(q) || (o.group || '').toLowerCase().includes(q)) : options
  }, [options, query])

  const chosen = new Set(selected)
  const summary =
    selected.length === 0
      ? `All`
      : selected.length === 1
        ? options.find((o) => o.id === selected[0])?.label || '1 selected'
        : `${selected.length} selected`

  function toggle(id: string) {
    onChange(chosen.has(id) ? selected.filter((s) => s !== id) : [...selected, id])
  }

  let lastGroup = ''
  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className={`inline-flex h-10 max-w-[14rem] items-center gap-1.5 rounded-lg border px-3 text-sm ${
          selected.length
            ? 'border-primary bg-primary/5 text-primary'
            : 'border-slate-300 bg-white text-slate-700 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200'
        }`}
      >
        <span className="text-slate-500">{label}:</span>
        <span className="truncate font-medium">{summary}</span>
        <span aria-hidden className="text-xs">▾</span>
      </button>
      {open ? (
        <div className="absolute left-0 z-30 mt-1 w-72 rounded-xl border border-slate-200 bg-white p-2 shadow-lg dark:border-slate-700 dark:bg-slate-800">
          {searchable ? (
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search…"
              aria-label={`Search ${label}`}
              className="mb-2 h-9 w-full rounded-lg border border-slate-300 px-3 text-sm dark:border-slate-600 dark:bg-slate-900"
              autoFocus
            />
          ) : null}
          <div className="mb-1 flex justify-between px-1 text-xs">
            <button type="button" onClick={() => onChange(Array.from(new Set([...selected, ...shown.map((o) => o.id)])))} className="font-medium text-primary">
              Select all
            </button>
            <button type="button" onClick={() => onChange([])} className="text-slate-500">
              Clear
            </button>
          </div>
          <ul className="max-h-72 overflow-y-auto" role="listbox" aria-multiselectable aria-label={label}>
            {shown.length === 0 ? <li className="px-2 py-3 text-sm text-slate-500">{emptyText}</li> : null}
            {shown.map((o) => {
              const header = o.group && o.group !== lastGroup ? o.group : ''
              lastGroup = o.group || lastGroup
              return (
                <li key={o.id}>
                  {header ? (
                    <p className="px-2 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-wide text-slate-400">{header}</p>
                  ) : null}
                  <label className="flex cursor-pointer items-center gap-2 rounded-lg px-2 py-1.5 text-sm hover:bg-slate-50 dark:hover:bg-slate-700/50">
                    <input type="checkbox" checked={chosen.has(o.id)} onChange={() => toggle(o.id)} className="size-4 rounded border-slate-300" />
                    <span className="text-slate-800 dark:text-slate-100">{o.label}</span>
                  </label>
                </li>
              )
            })}
          </ul>
        </div>
      ) : null}
    </div>
  )
}
